import React, { FC, useEffect, useState } from 'react';

import { Button, Col, notification, Row } from 'antd';
import routePaths from 'config/routes';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { logout } from 'src/actions/authActions';
import MCCheck from 'src/components/account/consumer/MCCheck';
import CompleteProfilePrompt from 'src/components/account/profile/completeProfilePrompt';
import Link from 'src/components/Link';
import { setFirstCompletedProfile } from 'src/actions/appActions';
import { IRootReducers } from 'src/reducers';
import { PlatformEnums, UserAccountStatusEnum } from 'src/shared/enums';
import { checkProfileCompletion, isProfileCompleted } from 'utils/accountUtils';
import classNames from 'classnames';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';

const { PLATFORM_TYPE } = getConfig().publicRuntimeConfig;

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ logout, setFirstCompletedProfile }, dispatch);

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const { MY_ACCOUNT } = routePaths.PAGES;
const { MY_PROFILE, MY_POINTS, MY_BONUS, MY_ADDRESSES, MY_ACADEMIC_RECORD } = MY_ACCOUNT;

const AccountLateralMenuConsumer: FC<Props> = ({
  logout,
  user,
  dermo,
  firstCompletedProfile,
  setFirstCompletedProfile,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const [showCompleteProfilePrompt, setShowCompleteProfilePrompt] = useState(
    Boolean(!isProfileCompleted(user?.actionList))
  );

  useEffect(() => {
    document.querySelector('body').scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setShowCompleteProfilePrompt(
      Boolean(!isProfileCompleted(user?.actionList))
    );
    return () => {
      if (firstCompletedProfile) {
        setFirstCompletedProfile(false);
      }
    };
  }, [firstCompletedProfile]);

  const titleClassName: string = classNames('user-menu-title', {
    'user-menu-title__progress-bar':
      firstCompletedProfile || showCompleteProfilePrompt,
  });
  const userMenuClassName = isProfileCompleted
    ? 'user-menu'
    : 'user-menu--incomplete';

  const getActiveLink = (href: string, icon = false): string => {
    if (router.pathname === href) return icon ? '' : 'user-menu-item__active';
    return icon ? 'icon-inactive' : '';
  };

  const handleLogout = async () => {
    try {
      await logout();

      notification['success']({
        message: formatMessage({ id: 'app.logout' }),
      });
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        notification['error']({
          message: formatMessage({ id: 'app.logout-error' }),
          description: `${data.code} ${data.error}`,
        });
      }
      console.error(error);
    }
  };

  const buildMenuLink = ({
    icon,
    route,
    title,
    platforms,
  }: {
    icon: string;
    route: string;
    title: string;
    platforms?: PlatformEnums[];
  }): JSX.Element => {
    if (platforms?.length && !platforms.includes(PLATFORM_TYPE)) return;

    // TODO: add future salesforce validation
    const isButtonAvailable = true;

    const className = isButtonAvailable
      ? `user-menu-section-title  ${getActiveLink(
          route
        )} user-menu-section-title__selectable`
      : `user-menu-section-title user-menu-section-title__disabled ${getActiveLink(
          route
        )}`;

    const button = (
      <Col className="user-menu-section">
        <Row className={className} align="middle">
          <i
            className={`icon icon--${icon} ${
              isButtonAvailable ? '' : 'icon-disabled'
            } ${getActiveLink(route, true)}`}
          />
          <FormattedMessage id={title} />
        </Row>
      </Col>
    );

    return isButtonAvailable ? <Link href={route}>{button}</Link> : button;
  };

  const renderCompleteProfilePrompt = () => {
    if (firstCompletedProfile || showCompleteProfilePrompt) {
      return (
        <CompleteProfilePrompt
          progress={checkProfileCompletion(user)}
          page={'profile'}
        />
      );
    }
  };

  return (
    <>
      {renderCompleteProfilePrompt()}
      <Col className={userMenuClassName}>
        <p className={titleClassName}>
          <FormattedMessage id="menu.header.my-account" />
        </p>
        {buildMenuLink({
          icon: 'puntos',
          title: 'menu.section.personal.points',
          route: MY_POINTS,
          platforms: [PlatformEnums.CON],
        })}
        {buildMenuLink({
          icon: 'ticket',
          title: 'menu.section.personal.coupons',
          route: MY_BONUS,
          platforms: [PlatformEnums.CON],
        })}
        {buildMenuLink({
          icon: 'myaccount',
          title: 'menu.section.personal.profile',
          route: MY_PROFILE,
          platforms: [PlatformEnums.CON],
        })}
        {buildMenuLink({
          icon: 'home',
          route: MY_ADDRESSES,
          title: 'menu.section.personal.addresses',
          platforms: [PlatformEnums.CON],
        })}
        {(dermo?.dermoStatus === UserAccountStatusEnum.GRANT) && dermo?.isDermo === true &&
          buildMenuLink({
            icon: 'academic-dermo',
            route: MY_ACADEMIC_RECORD,
            title: 'menu.section.personal.academic-record',
            platforms: [PlatformEnums.CON],
          })}
      </Col>
      <Row className="newsletter" justify="start" align="middle">
        <Col xs={24}>
          <div>
            <MCCheck user={user} />
          </div>
        </Col>
      </Row>
      <Row justify="start">
        <Button
          className="account-content-box btn-ghost btn-ghost--minimalist btn-ghost-logout"
          onClick={handleLogout}
        >
          <span className="action-label">
            <FormattedMessage id="menu.header.logout" />
          </span>
        </Button>
      </Row>
    </>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    dermo: state.auth.user?.dermo,
    firstCompletedProfile: state.app.firstCompletedProfile,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountLateralMenuConsumer);
